<template>
    <TransitionRoot as="template" :show="show">
        <Dialog as="div" class="fixed inset-0 overflow-hidden">
            <div class="absolute inset-0 overflow-hidden">
                <DialogOverlay class="absolute inset-0 bg-gray-500 opacity-75" @click="close"/>

                <div class="fixed inset-y-0 pl-16 max-w-full right-0 flex">
                    <TransitionChild as="template" enter="transform transition ease-in-out duration-500 sm:duration-700"
                                     enter-from="translate-x-full" enter-to="translate-x-0"
                                     leave="transform transition ease-in-out duration-500 sm:duration-700"
                                     leave-from="translate-x-0" leave-to="translate-x-full">
                        <div class="w-screen max-w-lg">
                            <form class="h-full divide-y divide-gray-200 flex flex-col bg-white shadow-xl"
                                  @submit.prevent="submit">
                                <div class="flex-1 h-0 overflow-y-auto">
                                    <div class="py-6 px-4  bg-amaze-coral sm:px-6">
                                        <div class="flex items-center justify-between">
                                            <DialogTitle class="text-lg text-white font-bold">
                                                <span v-if="form.id === null">Create Organisation Group</span>
                                                <span v-else>Update Organisation Group '{{ form.name }}'</span>
                                            </DialogTitle>
                                            <div class="ml-3 h-7 flex items-center">
                                                <button type="button"
                                                        class="bg-amaze-coral rounded-md text-indigo-200 hover:text-white focus:outline-none focus:ring-2 focus:ring-white"
                                                        @click="close()">
                                                    <span class="sr-only">Close panel</span>
                                                    <XIcon class="h-6 w-6" aria-hidden="true"/>
                                                </button>
                                            </div>
                                        </div>
                                        <div class="mt-1">
                                            <p class="text-sm text-white">
                                                Get started by filling in the information below to create an
                                                organisation group.
                                            </p>
                                        </div>
                                    </div>
                                    <SlideOutErrors/>
                                    <SlideoutFlashMessage ref="create_group_slideout_flash"/>
                                    <div class="flex-1 flex flex-col justify-between">
                                        <div class="px-4 divide-y divide-gray-200 sm:px-6">
                                            <div class="space-y-6 pt-6 pb-5">
                                                <div>
                                                    <label for="group-name"
                                                           class="block text-sm text-gray-900 required">
                                                        Name
                                                    </label>
                                                    <div class="mt-1">
                                                        <input type="text" id="group-name" v-model="form.name"
                                                               class="block w-full shadow-sm sm:text-sm focus:ring-indigo-500 focus:border-light-pitch-purple border-gray-300 rounded-md"
                                                               :class="{'border-red-500': form.errors.name}"
                                                        />
                                                    </div>
                                                </div>
                                                <h2 class="text-xl text-bold">Add Client To Group</h2>
                                                <Combobox as="div" v-model="selectedClient" nullable>
                                                    <ComboboxLabel class="block text-sm font-medium text-gray-700 required">
                                                        Assign Client
                                                    </ComboboxLabel>
                                                    <div class="relative mt-1">
                                                        <ComboboxInput
                                                            class="w-full rounded-md border border-gray-300 bg-white py-2 pl-3 pr-10 shadow-sm focus:border-light-pitch-purple focus:outline-none focus:ring-1 focus:ring-indigo-500 sm:text-sm"
                                                            @change="query = $event.target.value"
                                                            :display-value="(client) => typeof client !== 'undefined' ? client.name : ''"/>
                                                        <ComboboxButton
                                                            class="absolute inset-y-0 right-0 flex items-center rounded-r-md px-2 focus:outline-none">
                                                            <SelectorIcon class="h-5 w-5 text-gray-400"
                                                                          aria-hidden="true"/>
                                                        </ComboboxButton>

                                                        <ComboboxOptions v-if="filteredClients.length > 0"
                                                                         class="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
                                                            <ComboboxOption v-for="organisation in filteredClients"
                                                                            :key="organisation.id" :value="organisation"
                                                                            as="template" v-slot="{ active, selected }">
                                                                <li :class="['relative cursor-pointer select-none py-2 pl-4 pr-4', active ? 'bg-amaze-coral text-white' : 'text-gray-900']">
                                                                    <span :class="['block truncate', selected && 'font-semibold']">
                                                                      {{ organisation.name }}
                                                                    </span>

                                                                    <span v-if="selected"
                                                                          :class="['absolute inset-y-0 left-0 flex items-center pl-1.5', active ? 'text-white' : 'text-pitch-purple']">
                                                                      <CheckIcon class="h-5 w-5" aria-hidden="true"/>
                                                                    </span>
                                                                </li>
                                                            </ComboboxOption>
                                                        </ComboboxOptions>
                                                    </div>
                                                </Combobox>
                                                <div class="flex justify-end">
                                                    <button @click="addClient(selectedClient)" type="button"
                                                            class="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm rounded-md text-white bg-amaze-coral hover:bg-amaze-coral focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:bg-gray-500">
                                                        <span v-if="loading === true">Adding Organisation..</span>
                                                        <span v-else>Add Client</span>
                                                    </button>
                                                </div>

                                                <h2 class="text-xl text-bold">Organisations</h2>
                                                <div
                                                    class="shadow overflow-hidden border-b border-gray-200 sm:rounded-lg relative">

                                                    <table class="min-w-full divide-y divide-gray-200">
                                                        <thead class="bg-gray-50">
                                                        <tr>
                                                            <th scope="col"
                                                                class="px-6 py-3 text-left text-xs text-gray-500 uppercase tracking-wider">
                                                                Name
                                                            </th>
                                                            <th scope="col" class="relative px-6 py-3">
                                                                <span class="sr-only">Edit</span>
                                                            </th>
                                                        </tr>
                                                        </thead>
                                                        <tbody>
                                                        <tr v-if="form.organisations.length > 0"
                                                            v-for="(organisation, index) in form.organisations"
                                                            :class="{'bg-gray-100': index % 2}">
                                                            <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                                {{ organisation.name }}
                                                            </td>
                                                            <td class="px-6 w-1/5 py-4 whitespace-nowrap text-right text-sm">

                                                                <a @click="deleteClient(organisation)"
                                                                   class="text-red-600 hover:text-red-900 float-right hover:cursor-pointer">
                                                                    <svg xmlns="http://www.w3.org/2000/svg" fill="none"
                                                                         viewBox="0 0 24 24" class="w-4 h-4"
                                                                         stroke="currentColor">
                                                                        <path stroke-linecap="round"
                                                                              stroke-linejoin="round" stroke-width="2"
                                                                              d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path>
                                                                    </svg>
                                                                </a>
                                                            </td>
                                                        </tr>
                                                        <tr v-else>
                                                            <td colspan="2"
                                                                class="px-6 py-4 whitespace-nowrap text-sm text-gray-900"
                                                                :class="{'hidden':loading === true}">
                                                                There are no organisations added to this group.
                                                            </td>
                                                        </tr>
                                                        <tr v-if="loading === true && creating === true"
                                                            :class="{'animate-pulse':loading === true}">
                                                            <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                                <span class="h-4 w-full block bg-slate-200 rounded">&nbsp;</span>
                                                            </td>
                                                            <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                                <span class="h-4 w-full block bg-slate-200 rounded">&nbsp;</span>
                                                            </td>
                                                        </tr>
                                                        </tbody>
                                                    </table>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>


                                <div class="flex-shrink-0 px-4 py-4 flex justify-end">
                                    <button type="button"
                                            class="bg-white py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                                            @click="close">
                                        Close
                                    </button>
                                    <button :disabled="form.processing || !form.isDirty" type="submit"
                                            class=" ml-4 inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm rounded-md text-white bg-amaze-coral hover:bg-amaze-coral focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:bg-gray-200">
                                        <span v-if="form.id !== null">Update</span>
                                        <span v-else>Save</span>
                                    </button>
                                </div>
                            </form>
                        </div>
                    </TransitionChild>
                </div>
            </div>
        </Dialog>
    </TransitionRoot>
</template>
<script>
import {ref, reactive, computed} from 'vue'
import { mapState, mapActions } from 'vuex'
import {
    Dialog,
    DialogOverlay,
    DialogTitle,
    TransitionChild,
    TransitionRoot,
    Combobox,
    ComboboxButton,
    ComboboxInput,
    ComboboxLabel,
    ComboboxOption,
    ComboboxOptions
} from '@headlessui/vue'
import {XIcon} from '@heroicons/vue/outline'
import {LinkIcon, PlusSmIcon, QuestionMarkCircleIcon} from '@heroicons/vue/solid'
import {useForm, usePage} from '@inertiajs/vue3'
import {CheckIcon, SelectorIcon} from '@heroicons/vue/solid'

import SlideOutErrors from "@/Components/SlideOutErrors.vue";
import SlideoutFlashMessage from "@/Components/SlideoutFlashMessage";


export default {
    components: {
        SlideOutErrors,
        Dialog,
        DialogOverlay,
        DialogTitle,
        TransitionChild,
        TransitionRoot,
        LinkIcon,
        PlusSmIcon,
        QuestionMarkCircleIcon,
        XIcon,
        SlideoutFlashMessage,
        CheckIcon,
        SelectorIcon,
        Combobox,
        ComboboxInput,
        ComboboxLabel,
        ComboboxOptions,
        ComboboxOption,
        ComboboxButton
    },
    data() {
        return {
            form: useForm({
                id: null,
                name: null,
                organisations: []
            }),
            loading: false,
            query: '',
            selectedClient: ref(),
            creating: false
        }
    },
    created () {

    },
    methods: {
        close() {

            this.form.reset();
            this.form.clearErrors();
            this.closeCreateGroup();
            this.selectedClient = ref();
            this.$page.props.slideout_flash.success = null;
            this.$page.props.slideout_flash.error = null;
        },
        deleteClient(client) {
            this.form.organisations = this.form.organisations.filter(x => {
                return x.id !== client.id;
            })
        },
        addClient(client) {
            if(typeof client === 'undefined') {
                return false;
            }
            if(this.form.id !== null) {
                this.addClientToGroup(client);
            } else {
                this.form.organisations.push(client);
            }
        },
        submit() {
            if(this.form.id !== null) {
                this.form.post(route('frontend.organisations.groups.update', this.form.id), {
                    onSuccess: page => {
                        this.show = false;
                        this.showErrors = false;
                        this.form.reset();
                        this.closeCreateGroup();
                    },
                    onError: errors => {
                        this.showErrors = true;
                    }
                });
            } else {
                this.form.post(route('frontend.organisations.groups.create'), {
                    onSuccess: page => {
                        this.show = false;
                        this.showErrors = false;
                        this.form.reset();
                        this.closeCreateGroup();
                    },
                    onError: errors => {
                        this.showErrors = true;
                    }
                });
            }
        },
        ...mapActions('organisations', [
            'closeCreateGroup',
            'addClientToGroup',
            'deleteClientFromGroup'
        ])
    },

    computed: {
        ...mapState({
            organisations: state => state.organisations.clients ?? null,
            show: state => state.organisations.createGroupSlideOutOpen ?? null,
            group: state => state.organisations.group ?? null
        }),
        filteredClients: function() {
            return this.query === '' && this.query.length > 2
                ? this.organisations
                : this.organisations.filter((organisation) => {
                    return organisation.name.toLowerCase().includes(this.query.toLowerCase())
                })
        }
    },
    watch: {
        show: function(value) {
            if(value === true) {
                this.$store.dispatch('organisations/getAllClients')
                if(typeof this.group === 'object' && this.group !== null)   {
                    this.form.id = this.group.id;
                    this.form.name = this.group.name;
                    this.form.organisations = this.group.organisations;
                    this.selectedClient = ref();
                }
            }
        }
    }

}
</script>
